/**
 * priorityScore.ts — Priority Scoring Engine for Chanelle OS.
 *
 * Computes a numeric urgency score for any Action item so that the
 * My Tasks widget and Command Palette can surface the most important
 * items first, regardless of how the user has manually ordered them.
 *
 * Score formula:
 *   score = baseScore(priority)
 *           + overdueBonus(dueDate)
 *           + blockerBonus(status)
 *           - completedPenalty(status)
 *
 * The weights are configurable via UserSettings.priorityWeights,
 * falling back to DEFAULT_PRIORITY_WEIGHTS.
 */

import type { Action, PriorityWeights } from '../types';
import { DEFAULT_PRIORITY_WEIGHTS } from './defaults';

// -------------------------------------------------------
// Score computation
// -------------------------------------------------------

export function scoreItem(
  action: Action,
  weights: PriorityWeights = DEFAULT_PRIORITY_WEIGHTS
): number {
  let score = 0;

  // 1. Base score from priority label
  const ps = weights.priorityScores;
  switch (action.priority) {
    case 'Critical': score += ps.Critical; break;
    case 'High':     score += ps.High;     break;
    case 'Medium':   score += ps.Medium;   break;
    case 'Low':      score += ps.Low;      break;
    default:         score += ps.Medium;   break;
  }

  // 2. Overdue bonus — scales with days past due
  if (action.dueDate) {
    const dueMs  = new Date(action.dueDate).getTime();
    const nowMs  = Date.now();
    const dayMs  = 1000 * 60 * 60 * 24;
    const daysOverdue = Math.max(0, Math.floor((nowMs - dueMs) / dayMs));
    score += daysOverdue * weights.overdueMultiplier;
  }

  // 3. Status modifiers
  if (action.status === 'Blocked') {
    score += ps.High * 0.5; // Blocked items get a half-High bonus
  }
  if (action.status === 'Done') {
    score = 0; // Done items always score zero (pushed to the bottom)
  }

  // 4. Escalated items inherit an urgency bump
  if (action.escalated) {
    score += ps.High * 0.25;
  }

  return Math.max(0, score);
}

// -------------------------------------------------------
// Batch scoring + sorting
// -------------------------------------------------------

export function rankActions(
  actions: Action[],
  weights?: PriorityWeights
): Action[] {
  return [...actions].sort(
    (a, b) => scoreItem(b, weights) - scoreItem(a, weights)
  );
}

// -------------------------------------------------------
// Human-readable urgency label
// -------------------------------------------------------

export type UrgencyLevel = 'critical' | 'high' | 'medium' | 'low' | 'done';

export function getUrgencyLevel(
  action: Action,
  weights: PriorityWeights = DEFAULT_PRIORITY_WEIGHTS
): UrgencyLevel {
  if (action.status === 'Done') return 'done';
  const score = scoreItem(action, weights);
  if (score >= weights.priorityScores.Critical) return 'critical';
  if (score >= weights.priorityScores.High)     return 'high';
  if (score >= weights.priorityScores.Medium)   return 'medium';
  return 'low';
}

// -------------------------------------------------------
// Overdue helpers
// -------------------------------------------------------

export function isOverdue(action: Action): boolean {
  if (!action.dueDate || action.status === 'Done') return false;
  return new Date(action.dueDate) < new Date(new Date().toDateString());
}

export function daysUntilDue(action: Action): number | null {
  if (!action.dueDate) return null;
  const dueMs = new Date(action.dueDate).getTime();
  const nowMs = Date.now();
  return Math.ceil((dueMs - nowMs) / (1000 * 60 * 60 * 24));
}
